import {Spot, WalkLine, WalkLinePoint} from "../types";
import {buildSpotRuleGraph, SpotRuleGraph} from "../../pages/areaDetails/customFlow/spotRuleGraph";
import {v4 as uuidv4} from "uuid";

/**
 * Generate walk lines from the spot rule graph.
 * Each unique path from a start node through the DAG becomes a walk line.
 * At forks (multiple outgoing edges), the path splits into separate lines.
 * Merges are handled by keeping only one incoming line to the merge point.
 */
export const generateWalkLines = (spots: Spot[]): WalkLine[] => {
    const graph = buildSpotRuleGraph(spots);
    if (graph.startNodeIds.length === 0) {
        return [];
    }

    const lines: WalkLine[] = [];
    const visitedMergeTargets = new Set<string>();

    const walkPath = (startNodeId: string, initialPath: string[]) => {
        const path = [...initialPath];
        let current = startNodeId;

        while (true) {
            const incoming = graph.incomingByNodeId.get(current) || [];
            const isMerge = incoming.length > 1 && path.length > 0;

            if (isMerge && visitedMergeTargets.has(current)) {
                // Another path already continues past this merge — end here.
                path.push(current);
                break;
            }
            if (isMerge) {
                visitedMergeTargets.add(current);
            }

            path.push(current);
            const outgoing = graph.outgoingByNodeId.get(current) || [];

            if (outgoing.length === 0) {
                break;
            }
            if (outgoing.length === 1) {
                current = outgoing[0].toId;
                continue;
            }
            // Fork: spawn a new walk for each branch
            for (const edge of outgoing) {
                walkPath(edge.toId, [...path]);
            }
            return; // Current path ends at the fork node (included in each branch's initial path)
        }

        if (path.length >= 2) {
            lines.push(pathToWalkLine(graph, path));
        }
    };

    for (const startNodeId of graph.startNodeIds) {
        walkPath(startNodeId, []);
    }

    return deduplicateSegments(lines);
};

const pathToWalkLine = (graph: SpotRuleGraph, nodeIds: string[]): WalkLine => {
    const points: WalkLinePoint[] = nodeIds
        .map((nodeId) => {
            const spot = graph.spotByNodeId.get(nodeId);
            if (!spot) return undefined;
            return {spotKey: spot.key || spot.id} as WalkLinePoint;
        })
        .filter((p): p is WalkLinePoint => !!p);

    return {
        id: uuidv4(),
        points,
    };
};

/**
 * Reconcile existing walk lines against the current spot rule graph.
 * Preserves user-placed waypoints while adjusting for spot/rule changes:
 * - Deleted spots: removed from lines, adjacent waypoints preserved.
 * - Added spots with rules: inserted into the correct position.
 * - Rule removed: spot removed from the line if no other rule keeps it there.
 * - New fork: spawns a new branch line from the junction.
 * Lines with fewer than 2 points after reconciliation are dropped.
 */
export const reconcileWalkLines = (existingLines: WalkLine[], spots: Spot[]): WalkLine[] => {
    const graph = buildSpotRuleGraph(spots);

    // Build lookup: spotKey -> nodeId and nodeId -> spotKey
    const spotKeyToNodeId = new Map<string, string>();
    const nodeIdToSpotKey = new Map<string, string>();
    graph.spotByNodeId.forEach((spot, nodeId) => {
        const key = spot.key || spot.id;
        spotKeyToNodeId.set(key, nodeId);
        nodeIdToSpotKey.set(nodeId, key);
    });

    // Set of all valid spot IDs in the current graph
    const validSpotKeys = new Set(spotKeyToNodeId.keys());

    // Collect all spot IDs currently referenced across all existing walk lines
    const coveredEdges = new Set<string>(); // "fromSpotId->toSpotId"
    const reconciledLines: WalkLine[] = [];

    for (const line of existingLines) {
        // Step 1: Remove points referencing deleted spots
        const filtered = line.points.filter((p) => {
            if (p.spotKey) return validSpotKeys.has(p.spotKey);
            return true; // keep waypoints
        });

        if (filtered.length < 2) continue;

        // Step 2: Remove spot anchors whose connection to their neighbor no longer exists
        const connected = removeDisconnectedAnchors(filtered, graph, spotKeyToNodeId);

        if (connected.length < 2) continue;

        // Step 3: Insert newly connected spots between existing spot anchors
        const expanded = insertNewSpots(connected, graph, spotKeyToNodeId, nodeIdToSpotKey);

        if (expanded.length < 2) continue;

        // Track which edges this line covers
        const spotAnchors = expanded.filter((p) => p.spotKey);
        for (let i = 0; i < spotAnchors.length - 1; i++) {
            coveredEdges.add(`${spotAnchors[i].spotKey}->${spotAnchors[i + 1].spotKey}`);
        }

        reconciledLines.push({...line, points: expanded});
    }

    // Step 3: Create new branch lines for edges not covered by any existing line
    const newBranchLines = createMissingBranchLines(graph, nodeIdToSpotKey, coveredEdges);
    reconciledLines.push(...newBranchLines);

    return deduplicateSegments(reconciledLines);
};

/**
 * Remove spot anchors that are no longer connected to either neighbor in the
 * rule graph. A spot is kept if it has a reachable path (in either direction)
 * to at least one of its neighboring spot anchors.
 * Waypoints between two removed anchors are also dropped.
 */
const removeDisconnectedAnchors = (
    points: WalkLinePoint[],
    graph: SpotRuleGraph,
    spotKeyToNodeId: Map<string, string>,
): WalkLinePoint[] => {
    // Collect spot anchor indices
    const anchorIndices: number[] = [];
    for (let i = 0; i < points.length; i++) {
        if (points[i].spotKey) anchorIndices.push(i);
    }
    if (anchorIndices.length < 2) return points;

    // For each consecutive anchor pair, check if a path exists in the graph
    const connectedPairs = new Set<string>();
    for (let i = 0; i < anchorIndices.length - 1; i++) {
        const fromId = points[anchorIndices[i]].spotKey!;
        const toId = points[anchorIndices[i + 1]].spotKey!;
        const fromNode = spotKeyToNodeId.get(fromId);
        const toNode = spotKeyToNodeId.get(toId);
        if (fromNode && toNode && isReachable(graph, fromNode, toNode)) {
            connectedPairs.add(`${i}`); // mark pair index as connected
        }
    }

    // A spot anchor is kept if it participates in at least one connected pair
    const keepAnchor = new Set<number>();
    for (let i = 0; i < anchorIndices.length; i++) {
        // Check pair with previous anchor
        if (i > 0 && connectedPairs.has(`${i - 1}`)) {
            keepAnchor.add(anchorIndices[i]);
        }
        // Check pair with next anchor
        if (i < anchorIndices.length - 1 && connectedPairs.has(`${i}`)) {
            keepAnchor.add(anchorIndices[i]);
        }
    }

    // Rebuild: keep spot anchors that are connected, and waypoints between kept anchors
    const result: WalkLinePoint[] = [];
    for (let i = 0; i < points.length; i++) {
        if (points[i].spotKey) {
            if (keepAnchor.has(i)) {
                result.push(points[i]);
            }
        } else {
            // Waypoint: keep if the surrounding spot anchors are both kept
            const prevAnchorIdx = findPrevAnchorIndex(points, i);
            const nextAnchorIdx = findNextAnchorIndex(points, i);
            if (prevAnchorIdx !== -1 && nextAnchorIdx !== -1 &&
                keepAnchor.has(prevAnchorIdx) && keepAnchor.has(nextAnchorIdx)) {
                result.push(points[i]);
            }
        }
    }
    return result;
};

/** Check if toNodeId is reachable from fromNodeId via forward edges (BFS, max depth). */
const isReachable = (graph: SpotRuleGraph, fromNodeId: string, toNodeId: string): boolean => {
    const visited = new Set<string>();
    const queue = [fromNodeId];
    visited.add(fromNodeId);
    const maxSteps = 50;
    let steps = 0;

    while (queue.length > 0 && steps < maxSteps) {
        const current = queue.shift()!;
        steps++;
        const outgoing = graph.outgoingByNodeId.get(current) || [];
        for (const edge of outgoing) {
            if (edge.toId === toNodeId) return true;
            if (!visited.has(edge.toId)) {
                visited.add(edge.toId);
                queue.push(edge.toId);
            }
        }
    }
    return false;
};

const findPrevAnchorIndex = (points: WalkLinePoint[], from: number): number => {
    for (let i = from - 1; i >= 0; i--) {
        if (points[i].spotKey) return i;
    }
    return -1;
};

const findNextAnchorIndex = (points: WalkLinePoint[], from: number): number => {
    for (let i = from + 1; i < points.length; i++) {
        if (points[i].spotKey) return i;
    }
    return -1;
};

/**
 * Walk through a line's points and insert newly connected spots.
 * If spot A is followed by spot B, and the graph now has A -> C -> B,
 * insert C between A and B (after any waypoints between them).
 */
const insertNewSpots = (
    points: WalkLinePoint[],
    graph: SpotRuleGraph,
    spotKeyToNodeId: Map<string, string>,
    nodeIdToSpotKey: Map<string, string>,
): WalkLinePoint[] => {
    const result: WalkLinePoint[] = [];

    for (let i = 0; i < points.length; i++) {
        result.push(points[i]);

        // Look for the next spot anchor after this point
        if (!points[i].spotKey) continue;
        const fromSpotId = points[i].spotKey!;
        const fromNodeId = spotKeyToNodeId.get(fromSpotId);
        if (!fromNodeId) continue;

        // Find next spot anchor index
        let nextSpotIdx = -1;
        for (let j = i + 1; j < points.length; j++) {
            if (points[j].spotKey) {
                nextSpotIdx = j;
                break;
            }
        }
        if (nextSpotIdx === -1) continue;

        const toSpotId = points[nextSpotIdx].spotKey!;
        const toNodeId = spotKeyToNodeId.get(toSpotId);
        if (!toNodeId) continue;

        // Check if there are new intermediate spots on the path from -> to
        const intermediates = findIntermediateNodes(graph, fromNodeId, toNodeId);
        if (intermediates.length === 0) continue;

        // Collect waypoints between the two spot anchors
        const waypointsBetween: WalkLinePoint[] = [];
        for (let j = i + 1; j < nextSpotIdx; j++) {
            waypointsBetween.push(points[j]);
        }

        // Insert waypoints first, then the new spot anchors
        // (waypoints belong to the segment before the first new spot)
        for (const wp of waypointsBetween) {
            result.push(wp);
        }
        for (const nodeId of intermediates) {
            const spotKey = nodeIdToSpotKey.get(nodeId);
            if (spotKey) {
                result.push({spotKey});
            }
        }

        // Skip the waypoints we already added (they'll be skipped in the outer loop)
        i = nextSpotIdx - 1;
    }

    return result;
};

/**
 * Find nodes strictly between fromNodeId and toNodeId on any single linear path.
 * Returns [] if no intermediate nodes exist or if the path branches.
 */
const findIntermediateNodes = (graph: SpotRuleGraph, fromNodeId: string, toNodeId: string): string[] => {
    const intermediates: string[] = [];
    let current = fromNodeId;
    const maxSteps = 50; // safety limit

    for (let step = 0; step < maxSteps; step++) {
        const outgoing = graph.outgoingByNodeId.get(current) || [];
        if (outgoing.length !== 1) break; // fork or dead end
        const next = outgoing[0].toId;
        if (next === toNodeId) return intermediates;
        intermediates.push(next);
        current = next;
    }

    return []; // no linear path found
};

/**
 * Find graph edges not covered by any reconciled walk line and create new branch lines for them.
 */
const createMissingBranchLines = (
    graph: SpotRuleGraph,
    nodeIdToSpotKey: Map<string, string>,
    coveredEdges: Set<string>,
): WalkLine[] => {
    const newLines: WalkLine[] = [];

    graph.edges.forEach((edge) => {
        const fromSpotId = nodeIdToSpotKey.get(edge.fromId);
        const toSpotId = nodeIdToSpotKey.get(edge.toId);
        if (!fromSpotId || !toSpotId) return;

        const edgeKey = `${fromSpotId}->${toSpotId}`;
        if (coveredEdges.has(edgeKey)) return;

        // Walk forward from toId to build the new branch
        const branchPoints: WalkLinePoint[] = [{spotKey: fromSpotId}, {spotKey: toSpotId}];
        let current = edge.toId;
        const visited = new Set<string>([edge.fromId, edge.toId]);
        const maxSteps = 50;

        for (let step = 0; step < maxSteps; step++) {
            const outgoing = graph.outgoingByNodeId.get(current) || [];
            if (outgoing.length !== 1) break;
            const next = outgoing[0].toId;
            if (visited.has(next)) break;
            visited.add(next);

            const nextSpotId = nodeIdToSpotKey.get(next);
            if (!nextSpotId) break;

            const nextEdgeKey = `${nodeIdToSpotKey.get(current)}->${nextSpotId}`;
            if (coveredEdges.has(nextEdgeKey)) {
                // This edge is already covered — just add the junction and stop
                branchPoints.push({spotKey: nextSpotId});
                break;
            }

            branchPoints.push({spotKey: nextSpotId});
            coveredEdges.add(nextEdgeKey);
            current = next;
        }

        if (branchPoints.length >= 2) {
            coveredEdges.add(edgeKey);
            newLines.push({id: uuidv4(), points: branchPoints});
        }
    });

    return newLines;
};

/**
 * Remove duplicate spot-to-spot segments across walk lines.
 * When the same segment (spotA → spotB) appears in multiple lines, it is kept
 * in the line with more spot anchors (i.e. the longer/more complete path) and
 * removed from the other. Waypoints between the duplicate anchors are also
 * removed. Lines that drop below 2 points are discarded.
 */
const deduplicateSegments = (lines: WalkLine[]): WalkLine[] => {
    // For each segment key "spotA->spotB", record which line index owns it
    // (the one with the most spot anchors wins).
    const spotAnchorCounts = lines.map(
        (line) => line.points.filter((p) => p.spotKey).length
    );

    // segmentKey -> index of the owning line
    const segmentOwner = new Map<string, number>();

    for (let li = 0; li < lines.length; li++) {
        const anchors = getSpotAnchorPairs(lines[li]);
        for (const key of anchors) {
            const existing = segmentOwner.get(key);
            if (existing === undefined) {
                segmentOwner.set(key, li);
            } else if (spotAnchorCounts[li] > spotAnchorCounts[existing]) {
                segmentOwner.set(key, li);
            }
        }
    }

    // Now rebuild each line, skipping segments owned by a different line
    const result: WalkLine[] = [];
    for (let li = 0; li < lines.length; li++) {
        const cleaned = removeNonOwnedSegments(lines[li], li, segmentOwner);
        if (cleaned.points.length >= 2) {
            result.push(cleaned);
        }
    }
    return result;
};

/** Get all consecutive spot-anchor-pair keys for a line. */
const getSpotAnchorPairs = (line: WalkLine): string[] => {
    const keys: string[] = [];
    let prevSpotId: string | undefined;
    for (const p of line.points) {
        if (p.spotKey) {
            if (prevSpotId) {
                keys.push(`${prevSpotId}->${p.spotKey}`);
            }
            prevSpotId = p.spotKey;
        }
    }
    return keys;
};

/**
 * Remove segments (and their intermediate waypoints) from a line if
 * the segment is owned by a different line.
 */
const removeNonOwnedSegments = (
    line: WalkLine,
    lineIndex: number,
    segmentOwner: Map<string, number>,
): WalkLine => {
    const points = line.points;
    const kept: WalkLinePoint[] = [];
    let i = 0;

    while (i < points.length) {
        const current = points[i];

        if (!current.spotKey) {
            // Waypoint — only keep if we're not about to skip a segment
            kept.push(current);
            i++;
            continue;
        }

        // Find the next spot anchor
        let nextSpotIdx = -1;
        for (let j = i + 1; j < points.length; j++) {
            if (points[j].spotKey) {
                nextSpotIdx = j;
                break;
            }
        }

        if (nextSpotIdx === -1) {
            // Last spot anchor — keep it
            kept.push(current);
            i++;
            continue;
        }

        const segKey = `${current.spotKey}->${points[nextSpotIdx].spotKey}`;
        const owner = segmentOwner.get(segKey);

        if (owner !== undefined && owner !== lineIndex) {
            // This segment belongs to another line — skip the source anchor
            // and all waypoints up to (but not including) the next spot anchor.
            // The next spot anchor will be evaluated as a new segment start.
            i = nextSpotIdx;
            continue;
        }

        // Segment is ours — keep the source anchor
        kept.push(current);
        i++;
    }

    // Clean up: remove leading/trailing waypoints with no spot anchor
    const trimmed = trimOrphanedWaypoints(kept);
    return {...line, points: trimmed};
};

/** Remove waypoints that aren't between two spot anchors. */
const trimOrphanedWaypoints = (points: WalkLinePoint[]): WalkLinePoint[] => {
    // Find first and last spot anchor
    let firstAnchor = -1;
    let lastAnchor = -1;
    for (let i = 0; i < points.length; i++) {
        if (points[i].spotKey) {
            if (firstAnchor === -1) firstAnchor = i;
            lastAnchor = i;
        }
    }
    if (firstAnchor === -1) return [];
    return points.slice(firstAnchor, lastAnchor + 1);
};
